import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import Tabs from "../common/Tabs";
import Catalog from "./catalog";
import AuditLog from "./audit-log";
import Breadcrumb from "../common/Header/Breadcrumb";
import Drawer from "../common/Drawer";
import FileUpload from "../common/fileupload";
import Dropdown from "../common/dropdown";
import { catalogDropdownOptions } from "../../constants/columns/equipment-catalog/equipmentCatalog";
import { useEquipmentCatalog } from "../../facade/equipment-catalog/equipment-catalog.facade";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { DropdownOptions } from "../../utilities/commonUtilities";
import { useDispatch } from "react-redux";

const EquipmentCatalog: React.FC = () => {
    const { dropdownOption, file, fileError } = useAppSelector((state) => state?.equipmentCatalog);
    const [activeTab, setActiveTab] = useState<number>(0);
    const dispatch = useDispatch();
    const {
        loadEquipmentCatalog,
        loadExportEquipmentCatalog,
        loadAuditLog,
        updateFile,
        updateFileUploadProgress,
        updateFileUploadSuccess,
        updateFileUploading,
        updateFileError,
        validateUploadEquipment,
        updateExportEquipmentCatalog,
        updateDropdownOption
    } = useEquipmentCatalog();
    const [currentParams, setCurrentParams] = useState({
        PageNumber: 1,
        PageSize: 10,
        SortDto: { Field: "", Order: "" },
        GlobalFilter: "",
        CatalogType: dropdownOption?.id || "0"
    });
    const [downloadCategory, setDownloadCategory] = useState({ id: "0", name: "DR cables" });
    const [downloadParams, setDownloadParams] = useState({
        PageNumber: 1,
        PageSize: 10,
        SortDto: { Field: "", Order: "" },
        GlobalFilter: "",
        CatalogType: downloadCategory?.id || "0"
    });
    const [isExport, setIsExport] = useState({
        catalogExport: false,
        auditLogExport: false
    });
    const [showDrawer, setShowDrawer] = useState(false);
    const [File, setFile] = useState({
        fileName: file?.fileName,
        fileSize: file?.fileSize ?? 0,
        uploadProgress: file?.uploadProgress ?? 0,
        isSuccess: false,
        isUploading: file?.isUploading || false,
        impactedRows: file?.impactedRows || 0,
    });
    const [error, setError] = useState({
        error: fileError?.error || [],
        isValid: fileError?.isValid || true,
    });

    useEffect(() => {
        handleSelect({id:"0", name: "DR cables"});
        dispatch({ type: 'RESET_STORE' });
    }, []);

    useEffect(() => {
        if (activeTab === 0) {
            setIsExport((prev) => ({ ...prev, catalogExport: false }));
            loadEquipmentCatalog(currentParams);
        } else if (activeTab === 1) {
            setIsExport((prev) => ({ ...prev, auditLogExport: false }));
            loadAuditLog(currentParams);
        };
    }, [activeTab, currentParams]);

    useEffect(() => {
        if (isExport?.catalogExport) {
            loadExportEquipmentCatalog(downloadParams);
        } else if (activeTab === 1 && isExport?.auditLogExport) {
            // loadExportEquipmentCatalog(downloadParams);
        }
    }, [isExport, downloadParams]);

    const handleTabChange = (tab: number): void => {
        setActiveTab(tab);
        setIsExport((prev) => ({ ...prev, catalogExport: false, auditLogExport: false }));
        setCurrentParams({
            PageNumber: 1,
            PageSize: 10,
            SortDto: tab === 0 ? { Field: "", Order: "" } : { Field: "createdOn", Order: "Desc" },
            GlobalFilter: "",
            CatalogType: dropdownOption?.id || "0"
        });
    };

    const handleSelect = (option: DropdownOptions) => {
        updateDropdownOption(option);
        setIsExport((prev) => ({ ...prev, catalogExport: false, auditLogExport: false }));
        setCurrentParams({
            PageNumber: 1,
            PageSize: 10,
            SortDto: activeTab === 0 ? { Field: "", Order: "" } : { Field: "createdOn", Order: "Desc" },
            GlobalFilter: "",
            CatalogType: option?.id
        });
    };

    const handleDownload = () => {
        setIsExport((prev: any) => ({
            ...prev,
            catalogExport: true,
            auditLogExport: activeTab === 1 ? true : false
        }));
        setDownloadParams({
            PageNumber: 1,
            PageSize: 5000,
            CatalogType: downloadCategory?.id,
            GlobalFilter: "",
            SortDto: { Field: "", Order: "" },
        });
    };

    const handleFileChange = (File: File | null) => {
        updateFileUploading(true);
        updateFileUploadSuccess({
            fileName: File?.name,
            fileSize: File?.size,
            isSuccess: false,
            isUploading: true,
            impactedRows: 0,
        })
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setFile((prev: any) => ({ ...prev, uploadProgress: progress }));
            updateFileUploadProgress(progress);
            if (progress <= 90) {
                clearInterval(interval);
                updateFileUploadProgress(90);
                updateFileUploadSuccess({
                    fileName: File?.name,
                    fileSize: File?.size,
                    isSuccess: false,
                    isUploading: false,
                    impactedRows: 0,
                    uploadProgress: 90
                });
            }
        }, 100);
        const payload = {
            file: File,
            CatalogType: downloadCategory?.id
        };
        updateFile(File);
        validateUploadEquipment(payload);
    };

    const handleConfirm = () => {
        const payload = {
            File: file?.fileData,
            CatalogType: downloadCategory?.id
        }
        updateExportEquipmentCatalog(payload);
        handleClose(false);
    };

    const handleClose = (reopen: boolean) => {
        setShowDrawer(reopen ? true : false);
        updateFileUploadSuccess({
            fileName: "",
            fileSize: "",
            isSuccess: false,
            isUploading: false,
            impactedRows: 0,
            uploadProgress: 0,
        });
        updateFileUploadProgress(0);
        updateFile(null);
        updateFileError({
            error: [],
            isValid: false
        });
    };

    const tabs = [
        {
            label: `Catalog`,
            content: (
                <div className="mx-[12px] bg-darkcolor-800">
                    <Catalog
                        currentParams={currentParams}
                        setCurrentParams={setCurrentParams}
                    />
                </div>
            ),
        },
        {
            label: "Audit log",
            content: (
                <div className="px-[12px] bg-darkcolor-800">
                    <AuditLog
                        currentParams={currentParams}
                        setCurrentParams={setCurrentParams}
                    />
                </div>
            ),
        },
    ];

    const breadcrumbOptions = [
        { name: "Dashboard", link: "/dashboard" },
        { name: "DETERMINE catalog", link: window.location.pathname },
    ];

    return (
        <>
            <div>
                <div className="pt-[24px] flex flex-col w-full h-full gap-[8px] bg-darkcolor-700 sticky top-0 z-[1001]">
                    <div className="flex justify-between w-full items-end pl-[18px] pr-[32px]">
                        <div className="flex flex-col">
                            <Breadcrumb items={breadcrumbOptions} />
                        </div>
                    </div>
                    <ToastContainer />
                    <div className="flex items-center justify-between bg-darkcolor-700 border-b-[1px] border-darkcolor-400 pb-[20px]">
                        <div className="flex items-center gap-2 pl-[18px] pr-[32px]">
                            <p className="text-neutral-25 text-[23px] font-medium whitespace-nowrap">
                                Equipment catalog /
                            </p>
                            <Dropdown
                                options={catalogDropdownOptions}
                                onSelect={(value) => handleSelect(value)}
                                label=""
                                disabled={false}
                                bgColor="bg-darkcolor-700"
                                dropdownBgColor="bg-darkcolor-700"
                                selected={dropdownOption?.id ?? "0"}
                                dropdownWidth="200px"
                                height="350px"
                            />
                        </div>
                        <div className="pl-[18px] pr-[32px]">
                            <button
                                className="bg-primary-500 text-white px-4 py-2 rounded-lg"
                                onClick={() => {
                                    setShowDrawer(!showDrawer);
                                    setDownloadCategory(dropdownOption || { id: "0", name: "DR cables" });
                                    updateFile(null);
                                    setFile((prev: any) => ({
                                        ...prev,
                                        fileName: "",
                                        fileSize: 0,
                                        isSuccess: false,
                                    }))
                                }}
                            >
                                Bulk update equipment
                            </button>
                        </div>
                    </div>
                </div>
                <div className="bg-darkcolor-700 sticky top-[150px] equipment-catalog">
                    <Tabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onChange={handleTabChange}
                        bgColor="bg-darkcolor-800"
                        className=""
                    />
                </div>
                <Drawer
                    isOpen={showDrawer}
                    onClose={() => handleClose(false)}
                    title="Bulk update equipment"
                    width="w-[415px]"
                    bgColor="bg-darkcolor-800"
                    showButton={true}
                    confirmLabel={(!fileError?.isValid && file?.fileData && !file?.isUploading) ? "Start again" : ""}
                    disabled={file?.fileName === ""}
                    onConfirm={() => handleClose(true)}
                >
                    <div className="flex flex-col gap-4 p-4">
                        <Dropdown
                            options={catalogDropdownOptions}
                            placeholder="Select equipment type"
                            onSelect={(value) => {
                                setDownloadCategory(value);
                                handleClose(true);
                            }}
                            selected={downloadCategory?.id || "0"}
                            label="Equipment category"
                            bgColor="bg-darkcolor-800"
                            dropdownBgColor="bg-darkcolor-700"
                            height="350px"
                        />
                        <FileUpload
                            accept=".xlsx, .xls"
                            file={file}
                            setFile={setFile}
                            error={fileError}
                            setError={setError}
                            onFileChange={(File: any) => handleFileChange(File)}
                            onClickDownload={handleDownload}
                            onClickConfirm={handleConfirm}
                        />
                    </div>
                </Drawer>
            </div>
        </>
    );
};

export default EquipmentCatalog;
